/**
 * Create form for a visio background.
 */
import * as React from 'react';
import { ImageIcon } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { FileDropzone } from '@/components/common/FileDropzone';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { useFormValidation } from '@/hooks/useFormValidation';
import { visioBackgroundCreateValidationSchema } from '@/utils/validation-schemas-utils';
import { uploadVisioBackground } from '@/services/visio-backgrounds.service';
import type { CreateVisioBackgroundFormState } from '@/types/visio-background.types';
import {
  VISIO_BACKGROUND_ACCEPTED_FORMATS,
  VISIO_BACKGROUND_MAX_SIZE_BYTES,
} from './visio-background-configs';
import {
  applyVisioBackgroundFileSelect,
  applyVisioBackgroundName,
  emptyCreateVisioBackgroundForm,
} from './visio-background-form-utils';

/** Props for {@link CreateVisioBackgroundView}. */
interface CreateVisioBackgroundViewProps {
  onClose: () => void;
  onSuccess: () => void;
}

/** Create form for a visio background. */
export const CreateVisioBackgroundView: React.FC<Readonly<CreateVisioBackgroundViewProps>> = ({
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = React.useState<CreateVisioBackgroundFormState>(
    emptyCreateVisioBackgroundForm,
  );
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const validation = useFormValidation(visioBackgroundCreateValidationSchema);
  const formRef = React.useRef(formData);
  formRef.current = formData;

  React.useEffect(() => {
    if (!formData.file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(formData.file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [formData.file]);

  const syncFile = (next: CreateVisioBackgroundFormState) => {
    formRef.current = next;
    setFormData(next);
    validation.validateField('file', next.file, next);
    validation.validateField('name', next.name, next);
    validation.markAsTouched('file');
  };

  const handleFileSelect = (selected: File | null) => {
    syncFile(applyVisioBackgroundFileSelect(formRef.current, selected));
  };

  const handleFileError = (message: string | null) => {
    const next = { ...formRef.current, fileRejected: message !== null };
    formRef.current = next;
    setFormData(next);
    validation.validateField('file', next.file, next);
  };

  const uploadMutation = useMutation({
    mutationFn: () => uploadVisioBackground(formData.file as File, formData.name.trim()),
    onSuccess: () => {
      toast.success('Arrière-plan ajouté avec succès !');
      onSuccess();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const isValid = validation.isFormValid();

  /** Validate file + name, then POST multipart upload. */
  const handleSubmit = () => {
    if (!validation.validateForm(formData) || !formData.file) return;
    uploadMutation.mutate();
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updated = applyVisioBackgroundName(formRef.current, e.target.value);
    formRef.current = updated;
    setFormData(updated);
    validation.validateField('name', updated.name, updated);
    validation.markAsTouched('name');
    validation.markAsDirty('name');
  };

  const handleNameBlur = () => {
    validation.markAsTouched('name');
  };

  function renderPreview(): React.ReactNode {
    if (!previewUrl) return null;
    return (
      <img
        src={previewUrl}
        alt="Aperçu de l'arrière-plan"
        className="w-full aspect-video object-cover rounded-lg border border-border"
      />
    );
  }

  return (
    <SlidePanelForm
      formType="create"
      title="Ajouter un arrière-plan"
      subtitle="Renseignez les informations de l'arrière-plan"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={uploadMutation.isPending}
      isValid={isValid}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <ImageIcon className="w-4 h-4" />
          Image
        </h4>
        <div className="grid grid-cols-1 gap-4">
          <FileDropzone
            label="Fichier image"
            required
            accept={VISIO_BACKGROUND_ACCEPTED_FORMATS}
            maxSize={VISIO_BACKGROUND_MAX_SIZE_BYTES}
            onFileSelect={handleFileSelect}
            onError={handleFileError}
            forwardRejectedFile
            hint="JPG, PNG ou WEBP — taille maximale : 5 Mo — format paysage 16:9 recommandé"
          />
          {renderPreview()}
          <Input
            label="Nom"
            value={formData.name}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            placeholder="Ex: Bibliothèque"
            state={validation.getFieldState('name')}
            error={validation.getFieldError('name')}
            maxLength={120}
            required
            helperText="Nom affiché aux participants"
          />
        </div>
      </div>
    </SlidePanelForm>
  );
};
